/**
 * AviAutoCloseSection — Spec iter257 (2026-03-01).
 *
 * Éditeur du délai de clôture automatique des demandes AVI, par
 * sous-type (Information / Accusé / Validation). Réservé au Premier
 * Admin (l'API PUT côté backend fait respecter la règle).
 *
 * Backend :
 *   • GET  /api/avi/config/auto-close  (tout user auth)
 *   • PUT  /api/avi/config/auto-close  (Premier Admin uniquement)
 *
 * Comportement :
 *   • Chaque sous-type = un toggle "Activé" + un champ numérique (heures).
 *   • "Désactivé" → envoie ``null`` au backend (compte à rebours masqué
 *     dans le tableau AVI correspondant, aucun close auto).
 *   • Validation : la clôture automatique n'a **pas de sens** pour ce
 *     sous-type (une décision explicite doit être prise). On l'expose
 *     tout de même pour respecter la demande utilisateur, avec un
 *     bandeau ⚠️ signalant que l'activation force un statut
 *     ``approuvee`` par expiration — usage à la discrétion de l'admin.
 */

import { hasRight } from "@/components/crm/crmShared";
import { useEffect, useState } from "react";
import { guardedSave, useLeaveGuard } from "@/lib/leaveRegistry";
import { toast } from "@/lib/toast";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import { Timer, Save, AlertTriangle, Info as InfoIcon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const SUBTYPES = [
  {
    key: "information",
    label: "Information",
    color: "#0EA5E9",
    helper: "Clôture quand tous les destinataires ont ouvert.",
    defaultHours: 24,
  },
  {
    key: "accuse",
    label: "Accusé",
    color: "#F97316",
    helper: "Clôture quand tous les réceptionnaires ont ouvert.",
    defaultHours: 48,
  },
  {
    key: "validation",
    label: "Validation",
    color: "#22C55E",
    helper: "Clôture même sans décision explicite (rare).",
    defaultHours: 72,
    warn: "Activer la clôture auto sur Validation force un statut « approuvée » à l'expiration, sans réponse explicite du décideur. À réserver aux cas où l'absence de réponse vaut approbation.",
  },
];

export default function AviAutoCloseSection() {
  const { user } = useAuth();
  const canEdit = hasRight(user, "avi.config_autoclose"); // iter203 — droit de la matrice (plus de premier-admin en dur)

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // state ephemère : par sous-type { enabled: bool, hours: number|"" }
  const [values, setValues] = useState({
    information: { enabled: false, hours: 24 },
    accuse:      { enabled: false, hours: 48 },
    validation:  { enabled: false, hours: 72 },
  });
  const [initial, setInitial] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/avi/config/auto-close");
      const norm = {};
      SUBTYPES.forEach((s) => {
        const h = data?.[`${s.key}_hours`];
        norm[s.key] = {
          enabled: !!(h && h > 0),
          hours: h && h > 0 ? h : s.defaultHours,
        };
      });
      setValues(norm);
      setInitial(JSON.stringify(norm));
      setUpdatedAt(data?.updated_at || null);
    } catch (err) {
      toast.error("Impossible de charger la configuration", {
        description: err.response?.data?.detail || err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const dirty = initial && JSON.stringify(values) !== initial;

  useLeaveGuard({ dirty: !!dirty, label: "Paramètres · Clôture automatique des AVI", module: "parametres", save: guardedSave(() => save()) }); // eslint-disable-line no-use-before-define
  const save = async () => {
    if (!canEdit || saving) return;
    setSaving(true);
    try {
      const payload = {};
      SUBTYPES.forEach((s) => {
        const v = values[s.key];
        const h = parseInt(v.hours, 10);
        payload[`${s.key}_hours`] = (v.enabled && h > 0) ? h : null;
      });
      const { data } = await api.put("/avi/config/auto-close", payload);
      // recharge propre pour figer l'état "initial"
      const norm = {};
      SUBTYPES.forEach((s) => {
        const h = data?.[`${s.key}_hours`];
        norm[s.key] = {
          enabled: !!(h && h > 0),
          hours: h && h > 0 ? h : s.defaultHours,
        };
      });
      setValues(norm);
      setInitial(JSON.stringify(norm));
      setUpdatedAt(data?.updated_at || null);
      toast.success("Configuration enregistrée");
    } catch (err) {
      toast.error("Enregistrement impossible", {
        description: err.response?.data?.detail || err.message,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="bg-card border-vertec-borderDark p-5" data-testid="avi-auto-close-section">
      <div className="flex items-start gap-2.5 mb-4">
        <div
          className="rounded-md flex items-center justify-center shrink-0"
          style={{ width: 30, height: 30, background: "rgba(249,115,22,0.12)", color: "#F97316" }}
        >
          <Timer size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-base font-semibold leading-tight">
            Clôture automatique des demandes AVI
          </h2>
          <p className="text-muted-foreground text-[12px] leading-snug mt-0.5">
            Délai avant clôture auto quand <strong>tous</strong> les destinataires
            ont ouvert la demande. Colonne <em>Clôturing</em> visible dans le
            tableau AVI correspondant.
          </p>
          {!canEdit && (
            <div
              className="mt-2 inline-flex items-center gap-1.5 text-[10.5px] px-2 py-0.5 rounded-full"
              style={{
                background: "rgba(107,114,128,0.12)",
                color: "#9CA3AF",
                border: "1px solid rgba(107,114,128,0.30)",
              }}
              data-testid="avi-auto-close-readonly"
            >
              <InfoIcon size={10} />
              Lecture seule — seul le Premier Admin peut modifier.
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground">Chargement…</div>
      ) : (
        <TooltipProvider delayDuration={150}>
          <div className="space-y-2">
            {SUBTYPES.map((s) => {
              const v = values[s.key];
              return (
                <div
                  key={s.key}
                  className="flex items-center gap-3 pl-3 pr-3 py-2 rounded-md transition-colors"
                  style={{
                    background: "#141416",
                    border: "1px solid rgba(255,255,255,0.06)",
                    borderLeft: `3px solid ${s.color}`,
                  }}
                  data-testid={`avi-auto-close-row-${s.key}`}
                >
                  {/* Nom + avertissement discret (⚠ tooltip) — width fixé
                      pour aligner les colonnes des 3 lignes. */}
                  <div className="flex items-center gap-1.5 shrink-0" style={{ width: 138 }}>
                    <span className="text-[13px] font-semibold" style={{ color: "#EDEDED" }}>
                      {s.label}
                    </span>
                    {s.warn && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <AlertTriangle
                            size={13}
                            strokeWidth={2.3}
                            style={{ color: "#F87171", cursor: "help" }}
                            data-testid={`avi-auto-close-warn-${s.key}`}
                          />
                        </TooltipTrigger>
                        <TooltipContent
                          side="top"
                          className="max-w-xs text-[11.5px] leading-snug"
                          style={{
                            background: "#1a0e0e",
                            color: "#FCA5A5",
                            border: "1px solid rgba(239,68,68,0.35)",
                          }}
                        >
                          {s.warn}
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                  {/* Description courte — sur la même ligne, tronquée
                      si besoin. Grow pour absorber l'espace flexible. */}
                  <div
                    className="text-[11.5px] text-muted-foreground truncate flex-1 min-w-0"
                    title={s.helper}
                  >
                    {s.helper}
                  </div>
                  {/* Délai + h */}
                  <div className="flex items-center gap-1 shrink-0">
                    <Input
                      type="number"
                      min={1}
                      max={720}
                      value={v.hours}
                      onChange={(e) =>
                        setValues((prev) => ({
                          ...prev,
                          [s.key]: { ...prev[s.key], hours: e.target.value.replace(/[^0-9]/g, "") },
                        }))
                      }
                      disabled={!canEdit || !v.enabled}
                      className="h-8 tabular-nums text-center px-2"
                      style={{ width: 56 }}
                      data-testid={`avi-auto-close-hours-${s.key}`}
                    />
                    <span className="text-[11.5px] text-muted-foreground">h</span>
                  </div>
                  {/* Toggle Activé/Désactivé */}
                  <div className="flex items-center gap-2 shrink-0" style={{ width: 116 }}>
                    <span
                      className="text-[10px] uppercase tracking-widest tabular-nums text-right flex-1"
                      style={{ color: v.enabled ? s.color : "#6B7280" }}
                    >
                      {v.enabled ? "Activé" : "Désactivé"}
                    </span>
                    <Switch
                      checked={v.enabled}
                      onCheckedChange={(next) =>
                        setValues((prev) => ({ ...prev, [s.key]: { ...prev[s.key], enabled: next } }))
                      }
                      disabled={!canEdit}
                      data-testid={`avi-auto-close-switch-${s.key}`}
                    />
                  </div>
                </div>
              );
            })}

            <div className="flex items-center justify-between pt-2">
              <div className="text-[11px] text-muted-foreground">
                {updatedAt && (
                  <>Dernière modification : {new Date(updatedAt).toLocaleString("fr-FR")}</>
                )}
              </div>
              <Button
                onClick={save}
                disabled={!canEdit || !dirty || saving}
                className="gap-2"
                data-testid="avi-auto-close-save"
              >
                <Save size={14} />
                {saving ? "Enregistrement…" : "Enregistrer"}
              </Button>
            </div>
          </div>
        </TooltipProvider>
      )}
    </Card>
  );
}
